import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { TInputTag } from "components/InputMultipleTag";

import { useGetTicketListOptions } from "../../../UseCase/useGetTicketListOptions";
import { useTranslationContext } from "context/TranslationContext";
import { useGetPipelineStatusOptions } from "hooks/useGetPipelineStatusOptions";
import { useGetUsersPipeline } from "hooks/useGetUsersPipeline";
import { constructFilterFromParam } from "modules/MemberList/Client/Filter/useClientFilter";
import { UseGetTicketListGlobalOptions } from "modules/PastNotes/UseCase/useGetTicketListGlobalOpstions";

import { TGetNotesFilter } from "../../../Model/types";
import { EPipelineSlug } from "@type/api/management/types";
import { Value } from "react-calendar/dist/cjs/shared/types";
import getDatePayload from "modules/PastNotes/Model/helper/getDatePayload";

interface UseGetNotesFilterProps {
  filter?: Partial<TGetNotesFilter>;
  onFilter?: (filter: Partial<TGetNotesFilter>) => void;
  selectedTab?: string;
  pipelineSlug?: EPipelineSlug;
}

const useGetNotesFilter = ({
  filter,
  onFilter,
  selectedTab,
  pipelineSlug,
}: UseGetNotesFilterProps) => {
  const { buSlug } = useParams();
  const { language } = useTranslationContext();

  const [openFilter, setOpenFilter] = useState(false);
  const [staffTypeTags, setStaffTypeTags] = useState<TInputTag[]>([]);
  const [pipelineStatusTags, setPipelineStatusTags] = useState<TInputTag[]>([]);
  const [interviewTypeTags, setInterviewTypeTags] = useState<TInputTag[]>([]);
  const [ticketTags, setTicketTags] = useState<TInputTag[]>([]);
  const [dateFilter, setDateFilter] = useState({ start: "", end: "" });

  const { pipelineStatusOptions, isLoadingPipelineStatusOptions } =
    useGetPipelineStatusOptions({
      pipelineSlug,
      language,
    });

  const { userPipelines } = useGetUsersPipeline({
    buId: buSlug,
    language,
  });

  const idPipeline = useMemo(() => {
    return userPipelines?.find((i) => i.slug === pipelineSlug)?.id || undefined;
  }, [pipelineSlug, userPipelines]);

  const { ticketListOptions } = useGetTicketListOptions({
    idBu: buSlug || "",
    excludedTicketCode: "AHT-",
    idPipeline,
    enabled: !!idPipeline,
  });

  const { ticketListGlobalOptions } = UseGetTicketListGlobalOptions({
    idPipeline,
  });

  const isEmptyFilter = useMemo(() => {
    const existingFilter: Partial<TGetNotesFilter> = {
      staffType: filter?.staffType,
      idPipelineStatus: filter?.idPipelineStatus,
      interviewType: filter?.interviewType,
      idTicket: filter?.idTicket,
      startDate: filter?.startDate,
      endDate: filter?.endDate,
    };

    for (const key in existingFilter) {
      if (existingFilter[key as keyof TGetNotesFilter]) return false;
    }
    return true;
  }, [
    filter?.endDate,
    filter?.idPipelineStatus,
    filter?.idTicket,
    filter?.interviewType,
    filter?.staffType,
    filter?.startDate,
  ]);

  const handleChangeDate = (value: Value) => {
    if (
      Array.isArray(value) &&
      value?.[0]?.toString() &&
      value?.[1]?.toString()
    ) {
      setDateFilter({
        start: getDatePayload(value[0]),
        end: getDatePayload(value[1], true),
      });
    }
  };

  const handleSubmitFilter = () => {
    const submittedFilter: Partial<TGetNotesFilter> = {
      idPipelineStatus: pipelineStatusTags.map((i) => i.id).join(","),
      idTicket: ticketTags.map((i) => i.id).join(","),
      startDate: dateFilter.start || undefined,
      staffType: staffTypeTags.map((i) => i.id).join(","),
      interviewType: interviewTypeTags.map((i) => i.id).join(","),
      endDate: dateFilter.end || undefined,
    };

    if (selectedTab !== "fieldSales") {
      submittedFilter.staffType = staffTypeTags.map((i) => i.id).join(",");
      submittedFilter.interviewType = interviewTypeTags
        .map((i) => i.id)
        .join(",");
    }
    onFilter?.(submittedFilter);
    setOpenFilter(false);
  };

  const handleResetFilter = () => {
    setDateFilter({ start: "", end: "" });
    setStaffTypeTags([]);
    setInterviewTypeTags([]);
    setPipelineStatusTags([]);
    setTicketTags([]);

    onFilter?.({
      idPipelineStatus: undefined,
      idTicket: undefined,
      startDate: undefined,
      endDate: undefined,
      staffType: undefined,
      interviewType: undefined,
    });
  };

  const handleOpenFilter = () => setOpenFilter((prev) => !prev);
  const handleCloseFilter = () => setOpenFilter(false);

  const handleCloseAndResetFilter = () => {
    handleCloseFilter();
    handleResetFilter();
  };

  useEffect(() => {
    if (filter?.startDate && filter?.endDate) {
      setDateFilter({
        start: filter.startDate,
        end: filter.endDate,
      });
    }
  }, [filter?.startDate, filter?.endDate]);

  useEffect(() => {
    if (filter?.idPipelineStatus && pipelineStatusOptions?.length) {
      setPipelineStatusTags(
        constructFilterFromParam(pipelineStatusOptions, filter.idPipelineStatus)
      );
    }
  }, [filter?.idPipelineStatus, pipelineStatusOptions]);

  return {
    ticketListOptions,
    ticketListGlobalOptions,
    interviewTypeTags,
    pipelineStatusTags,
    openFilter,
    staffTypeTags,
    isLoading: isLoadingPipelineStatusOptions,
    isEmptyFilter,
    pipelineStatusOptions,
    ticketTags,
    dateFilter,
    setTicketTags,
    setStaffTypeTags,
    setInterviewTypeTags,
    setPipelineStatusTags,
    onCloseFilter: handleCloseFilter,
    onOpenFilter: handleOpenFilter,
    onSubmitFilter: handleSubmitFilter,
    onResetFilter: handleResetFilter,
    onChangeDate: handleChangeDate,
    onCloseAndResetFilter: handleCloseAndResetFilter,
  };
};

export default useGetNotesFilter;
